import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCircleCheck, faDownload, faEye } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';
import Title from 'Components/Title';
import { CustomerDetails, ProjectOverviewItem, Standort, StoredProject } from 'apiClient/Api';
import { CalculateResponse } from './calculatorApi';
import { withDefaultStandort } from './instanceModel';
import { Warenkorb } from './Warenkorb';
import { CustomerNoteBanner } from './CustomerNoteBanner';
import { CustomerDetailsView } from './CustomerDetails';

interface OfferItem {
    id?: string;
    fileName?: string;
    offerNumber?: string;
    totalSum?: number;
    released?: boolean;
    acceptedAt?: string;
    acceptedByName?: string;
}

const money = (v?: number | null) =>
    v == null
        ? null
        : Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const dateTime = (v?: string | null) => (v ? new Date(v).toLocaleString('de-DE') : '');

/**
 * Lesende Ansicht „Meine Kalkulationen" für Personen ohne Bedien-Recht (calculator.operate):
 * listet die sichtbaren Projekte (eigene + freigegebene) und öffnet eine read-only Detailansicht
 * über GET /Calculator/project/{id}/calculate (preis-sicher redigiert) inkl. freigegebener Angebote.
 */
export const MyCalculations: React.FC = () => {
    const { t } = useTranslation();
    const { setTitle, api, apiError } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();

    const [items, setItems] = useState<ProjectOverviewItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);

    const [detailLoading, setDetailLoading] = useState(false);
    const [response, setResponse] = useState<CalculateResponse | null>(null);
    const [standorte, setStandorte] = useState<Standort[]>([]);
    const [offers, setOffers] = useState<OfferItem[]>([]);
    const [notes, setNotes] = useState<string | null>(null);
    const [details, setDetails] = useState<CustomerDetails | null>(null);

    useEffect(() => setTitle(t('calculation.mine.title', 'Meine Kalkulationen')), [setTitle, t]);

    const reload = useCallback(() => {
        setLoading(true);
        api.calculator
            .projectOverviewGetOverview()
            .then((r) => setItems(r.data ?? []))
            .catch(apiError)
            .finally(() => setLoading(false));
    }, [api, apiError]);

    useEffect(() => reload(), [reload]);

    const openDetail = async (id: string, name: string) => {
        setSelected({ id, name });
        setDetailLoading(true);
        setResponse(null);
        setOffers([]);
        setStandorte([]);
        setNotes(null);
        setDetails(null);
        try {
            const [proj, calc, offs] = await Promise.all([
                api.calculator.projectsGet(id),
                api.calculator.calculatorCalculateStoredProject(id),
                api.calculator.projectOfferList(id).catch(() => ({ data: [] as unknown })),
            ]);
            const sp = proj.data as unknown as StoredProject;
            setStandorte(withDefaultStandort(sp.project?.standorte));
            setNotes((sp as unknown as { customerNotes?: string | null }).customerNotes ?? null);
            setDetails((sp as unknown as { customerDetails?: CustomerDetails | null }).customerDetails ?? null);
            setResponse(calc.data as unknown as CalculateResponse);
            setOffers(((offs.data as unknown as OfferItem[]) || []).filter((o) => o.released));
        } catch (e) {
            apiError(e);
        } finally {
            setDetailLoading(false);
        }
    };

    const download = async (offerId: string, fileName?: string) => {
        if (!selected) return;
        try {
            const r = await api.calculator.projectOfferDownload(selected.id, offerId, { format: 'blob' });
            const url = URL.createObjectURL(r.data as unknown as Blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName || 'angebot.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            enqueueSnackbar(t('calculation.mine.downloadFailed', 'Download fehlgeschlagen.'), { variant: 'error' });
        }
    };

    const sorted = useMemo(
        () => [...items].sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '')),
        [items],
    );

    const pricesVisible = response?.pricesVisible !== false;

    // ---- Detailansicht ----
    if (selected) {
        return (
            <Grid size={12}>
                <Title>{t('calculation.mine.title', 'Meine Kalkulationen')}</Title>
                <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
                    <Button
                        color="inherit"
                        startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                        onClick={() => setSelected(null)}
                    >
                        {t('calculation.mine.back', 'Zurück')}
                    </Button>
                    <Typography variant="h6">{selected.name}</Typography>
                </Stack>

                {detailLoading && <LinearProgress sx={{ mb: 2 }} />}

                <CustomerNoteBanner note={notes} />

                {response && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Warenkorb
                            positions={response.result.positions}
                            summary={response.result.summary}
                            standorte={standorte}
                            pricesVisible={pricesVisible}
                        />
                    </Paper>
                )}

                {details && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('calculation.mine.customerDetails', 'Kundendaten & Anschrift')}
                        </Typography>
                        <CustomerDetailsView details={details} />
                    </Paper>
                )}

                {offers.length > 0 && (
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('calculation.mine.offers', 'Ihre Angebote')}
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        {offers.map((o) => (
                            <Paper key={o.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                                    useFlexGap
                                >
                                    <Typography sx={{ fontWeight: 600 }}>
                                        {t('calculation.mine.offer', 'Angebot')} {o.offerNumber || ''}
                                        {o.totalSum != null ? ` · ${money(o.totalSum)} netto` : ''}
                                    </Typography>
                                    {o.acceptedAt && (
                                        <Chip
                                            size="small"
                                            color="success"
                                            icon={<FontAwesomeIcon icon={faCircleCheck} />}
                                            label={t('calculation.mine.accepted', 'Angenommen')}
                                        />
                                    )}
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Button
                                        startIcon={<FontAwesomeIcon icon={faDownload} />}
                                        onClick={() => o.id && download(o.id, o.fileName)}
                                    >
                                        {t('calculation.mine.download', 'PDF herunterladen')}
                                    </Button>
                                </Stack>
                            </Paper>
                        ))}
                    </Paper>
                )}

                {!detailLoading && !response && (
                    <Alert severity="info">{t('calculation.mine.noDetail', 'Für dieses Projekt liegt keine Kalkulation vor.')}</Alert>
                )}
            </Grid>
        );
    }

    // ---- Liste ----
    return (
        <Grid size={12}>
            <Title>{t('calculation.mine.title', 'Meine Kalkulationen')}</Title>
            <Paper sx={{ p: 2 }}>
                {loading && <LinearProgress sx={{ mb: 1 }} />}
                {!loading && sorted.length === 0 && (
                    <Typography color="text.secondary">
                        {t('calculation.mine.empty', 'Ihnen wurden noch keine Kalkulationen zugewiesen.')}
                    </Typography>
                )}
                {sorted.length > 0 && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('calculation.store.name')}</TableCell>
                                <TableCell>{t('calculation.overview.offers', 'Angebote')}</TableCell>
                                <TableCell align="right">{t('calculation.store.lastCalc')}</TableCell>
                                <TableCell>{t('calculation.store.updated')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map((p) => (
                                <TableRow key={p.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {p.name}
                                        </Typography>
                                        {p.customerName && (
                                            <Typography variant="caption" color="text.secondary">
                                                {p.customerName}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {p.offers?.approved ? (
                                            <Chip
                                                size="small"
                                                color="success"
                                                label={t('calculation.overview.approved', { count: p.offers.approved })}
                                            />
                                        ) : p.offers?.released ? (
                                            <Chip
                                                size="small"
                                                color="primary"
                                                label={t('calculation.overview.released', { count: p.offers.released })}
                                            />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                –
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {money(p.lastCalcTotalNet) ? (
                                            <Typography variant="body2">{money(p.lastCalcTotalNet)}</Typography>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                –
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{dateTime(p.updatedAt)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<FontAwesomeIcon icon={faEye} />}
                                            onClick={() => p.id && openDetail(p.id, p.name || '')}
                                        >
                                            {t('calculation.mine.view', 'Ansehen')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>
        </Grid>
    );
};

export default MyCalculations;
